import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const interceptorRef = useRef(null);
  const isLoggingOut = useRef(false);

  // Logout function - must be defined before useEffect that uses it
  const logout = useCallback(() => {
    // Prevent multiple logout calls
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setSessionExpired(false);
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
    
    // Force navigation to login to prevent any lingering state
    if (window.location.pathname !== '/admin/login' && window.location.pathname !== '/login') {
      window.location.href = '/admin/login';
    }
  }, []);

  // Handle session expiration gracefully
  const handleSessionExpired = useCallback(() => {
    if (isLoggingOut.current) return;
    
    console.warn('[Auth] Session expired - logging out');
    setSessionExpired(true);
    logout();
  }, [logout]);

  // Setup axios interceptor for global 401 handling
  useEffect(() => {
    // Remove any existing interceptor
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }

    // Add response interceptor to catch 401/403 errors globally
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle authentication errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          const errorMessage = error.response?.data?.detail || 'Session expired';
          console.error(`[Auth] ${error.response.status} Error:`, errorMessage);
          
          // Don't logout if we're already on login page or if it's a login attempt
          const isLoginAttempt = error.config?.url?.includes('/auth/login');
          const isOnLoginPage = window.location.pathname.includes('/login');
          
          if (!isLoginAttempt && !isOnLoginPage) {
            handleSessionExpired();
          }
          
          return Promise.reject(new Error(errorMessage));
        }
        return Promise.reject(error);
      }
    );

    // Cleanup on unmount
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [handleSessionExpired]);

  // Fetch user on token change
  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('[Auth] Failed to fetch user:', error.message);
        // The interceptor will handle 401/403, so we only need to handle other errors here
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    isLoggingOut.current = false;
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    isLoggingOut.current = false;
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, sessionExpired, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
